import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { redirectToStripe } from "@/lib/redirect";

/**
 * One-click Stripe Checkout for Linktree / IG bio / poster QR.
 *
 * URL pattern:  /buy/:slug   →   either:
 *   • non-ticket items: cart is created and Stripe opens immediately
 *   • ticket items:     a brief add-ons step is shown ("add vinyl / WAV?")
 *                       so the buyer can attach Metamorph downloads or LP
 *                       before paying. They click Continue → Stripe.
 *
 * Slugs:
 *   /buy/sydney         →  1 × Sydney ticket           (general)
 *   /buy/sydney-artist  →  1 × Sydney artist ticket    (afterparty + champagne)
 *   /buy/adelaide       →  1 × Adelaide
 *   /buy/adelaide-artist
 *   /buy/london         →  1 × London (£55 GBP)
 *   /buy/london-artist
 *   /buy/wav            →  WAV pre-order @ $20 PWYC
 *   /buy/wav-15         →  WAV @ $15
 *   /buy/film, /buy/film-25, /buy/uphill, /buy/uphill-10
 */
const TICKET_CITIES = new Set(["sydney", "sydney_2", "adelaide", "london", "melbourne", "paris", "perth", "gold_coast"]);

function parseSlug(slug) {
  const raw = (slug || "").toLowerCase().trim();
  const s = raw.replace(/-/g, "_");
  // Ticket slugs — base city or `<city>-artist` (also `<city>_artist`)
  const artistMatch = s.match(/^(sydney|sydney_2|adelaide|london|melbourne|paris|perth|gold_coast)_artist$/);
  if (artistMatch) {
    return { type: "ticket", city: artistMatch[1], quantity: 1, tier: "artist" };
  }
  if (TICKET_CITIES.has(s)) {
    return { type: "ticket", city: s, quantity: 1, tier: "general" };
  }
  // PWYC custom-price variants ("wav-30", "film-25", "uphill-10")
  const m = s.match(/^(wav|film|uphill)-(\d+(?:\.\d+)?)$/);
  if (m) {
    const base = { wav: { type: "wav", custom_price: 20, quantity: 1 },
                   film: { type: "myth_film", custom_price: 20, quantity: 1 },
                   uphill: { type: "uphill", custom_price: 5, quantity: 1 } }[m[1]];
    return base ? { ...base, custom_price: parseFloat(m[2]) } : null;
  }
  if (s === "wav") return { type: "wav", custom_price: 20, quantity: 1 };
  if (s === "film") return { type: "myth_film", custom_price: 20, quantity: 1 };
  if (s === "uphill") return { type: "uphill", custom_price: 5, quantity: 1 };
  return null;
}

export default function QuickCheckout() {
  const { slug } = useParams();
  const item = useMemo(() => parseSlug(slug), [slug]);
  const isTicket = item?.type === "ticket";

  const [error, setError] = useState(null);
  const [shows, setShows] = useState(null);     // /api/shows payload
  const [addons, setAddons] = useState({ wav: false, vinylReserve: false });
  const [wavAmount, setWavAmount] = useState(20);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const startedRef = useRef(false);

  // Hydrate the show + add-on prices when we're in ticket flow
  useEffect(() => {
    if (!isTicket) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/shows");
        if (alive) setShows(data.shows || []);
      } catch {
        if (alive) setShows([]);
      }
    })();
    return () => { alive = false; };
  }, [isTicket]);

  // For non-ticket flows, redirect immediately (legacy behaviour)
  useEffect(() => {
    if (isTicket || !item || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/checkout/cart", {
          items: [item],
          origin_url: window.location.origin,
        });
        if (data?.url) redirectToStripe(data.url);
        else setError("Checkout could not be started. Please try again.");
      } catch (e) {
        setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
      }
    })();
  }, [isTicket, item]);

  const show = isTicket && shows ? shows.find((s) => s.city === item.city) : null;
  const ticketUnitPrice = show ? show.price : 0;
  const currencySymbol = show?.currency_symbol || "$";
  const maxQty = Math.min(10, Math.max(1, show?.remaining ?? 10));
  const subtotal = useMemo(() => {
    if (!show) return 0;
    let t = ticketUnitPrice * quantity;
    if (addons.wav) t += Math.max(0, Number(wavAmount) || 0);
    // Vinyl is a reservation (no charge here) — handled post-payment.
    return t;
  }, [show, addons, wavAmount, ticketUnitPrice, quantity]);

  if (!item) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-[var(--mm-bg,#050a1a)] text-white px-6" data-testid="quick-checkout">
        <h1 className="deco-text text-3xl uppercase mb-4" style={{ letterSpacing: "0.08em" }}>One moment…</h1>
        <p className="font-mono text-sm text-white/70 mb-6" data-testid="quick-checkout-error">Unknown checkout link: {slug}</p>
        <a href="/tickets" className="btn-line inline-flex" data-testid="quick-checkout-back">View all shows →</a>
      </main>
    );
  }

  if (!isTicket) {
    // Non-ticket flow — redirecting state
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-[var(--mm-bg,#050a1a)] text-white px-6" data-testid="quick-checkout">
        {error ? (
          <div className="text-center max-w-md">
            <h1 className="deco-text text-3xl uppercase mb-4" style={{ letterSpacing: "0.08em" }}>One moment&hellip;</h1>
            <p className="font-mono text-sm text-white/70 mb-6" data-testid="quick-checkout-error">{error}</p>
            <a href="/tickets" className="btn-line inline-flex" data-testid="quick-checkout-back">View all shows →</a>
          </div>
        ) : (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.4em] text-white/55 mb-3">Securing your spot</p>
            <h1 className="deco-text text-4xl sm:text-5xl uppercase mb-2 text-center" style={{ letterSpacing: "0.1em" }}>Metamorph</h1>
            <p className="font-serif italic text-sm text-white/60 mt-4">Redirecting to secure checkout&hellip;</p>
            <div className="mt-8 w-8 h-8 border border-white/20 border-t-[var(--mm-accent)] rounded-full animate-spin" aria-hidden />
          </>
        )}
      </main>
    );
  }

  // Ticket flow — show add-ons step
  const onContinue = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const items = [{ ...item, quantity: Math.max(1, Math.min(quantity, maxQty)) }];
    if (addons.wav) {
      const amt = Math.max(1, Number(wavAmount) || 20);
      items.push({ type: "wav", custom_price: amt, quantity: 1 });
    }
    try {
      const { data } = await api.post("/checkout/cart", {
        items,
        origin_url: window.location.origin,
        wants_vinyl_reserve: !!addons.vinylReserve,
      });
      if (data?.url) redirectToStripe(data.url);
      else {
        setError("Checkout could not be started. Please try again.");
        setSubmitting(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center bg-[var(--mm-bg,#050a1a)] text-white px-6 py-12" data-testid="quick-checkout">
      <div className="w-full max-w-md">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/55 text-center mb-3">
          {item.tier === "artist" ? "Artist ticket" : "Ticket"}
        </p>
        <h1 className="deco-text text-4xl sm:text-5xl uppercase text-center leading-[0.95] mb-3" style={{ letterSpacing: "0.1em" }}>
          Metamorph
        </h1>

        {show ? (
          <>
            <h2 className="text-xl sm:text-2xl text-center mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3f2ee", letterSpacing: "0.02em", fontWeight: 500 }}>
              {show.label}
              {item.tier === "artist" ? " · Artist" : ""}
            </h2>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 text-center mb-1">
              {show.date}
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 text-center mb-8">
              {show.venue || "Venue announced soon"}
            </p>

            {item.tier === "artist" && (
              <div className="border px-4 py-3 mb-6" style={{ borderColor: "rgba(243,224,166,0.32)", background: "rgba(243,224,166,0.05)" }}>
                <p className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--mm-accent)" }}>
                  Artist ticket
                </p>
              </div>
            )}

            {/* Quantity stepper — capped by venue remaining capacity */}
            <div className="border-t border-white/15 pt-4 mb-2 flex items-center justify-between" data-testid="quantity-row">
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/70">Tickets</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="w-9 h-9 border border-white/25 hover:border-[var(--mm-accent)] disabled:opacity-25 disabled:cursor-not-allowed text-lg leading-none transition"
                  aria-label="Decrease quantity"
                  data-testid="quantity-decrement"
                >&minus;</button>
                <span
                  className="tabular-nums text-white min-w-[1.5rem] text-center"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "22px", fontWeight: 500 }}
                  data-testid="quantity-value"
                >
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                  disabled={quantity >= maxQty}
                  className="w-9 h-9 border border-white/25 hover:border-[var(--mm-accent)] disabled:opacity-25 disabled:cursor-not-allowed text-lg leading-none transition"
                  aria-label="Increase quantity"
                  data-testid="quantity-increment"
                >+</button>
              </div>
            </div>

            <div className="pt-3 mb-5">
              <div className="flex justify-between items-baseline">
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/70">Total</span>
                <span className="tabular-nums text-[var(--mm-accent)]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "28px", fontWeight: 500, letterSpacing: "0.01em" }} data-testid="quick-checkout-total">
                  {currencySymbol}{subtotal.toFixed(2)}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={onContinue}
              disabled={submitting}
              className="btn-line w-full justify-center text-sm py-3 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="quick-checkout-continue"
            >
              {submitting ? "Securing your spot…" : (<>Continue to payment <ArrowRight className="w-4 h-4" strokeWidth={1.4} /></>)}
            </button>

            {error && (
              <p className="font-mono text-[11px] text-rose-300/80 text-center mt-4" data-testid="quick-checkout-error">{error}</p>
            )}

            <p className="font-mono text-[9px] uppercase tracking-[0.32em] text-white/35 text-center mt-5">
              Stripe Checkout · Apple Pay / Google Pay / Card
            </p>

            {/* Add-ons sit BELOW the payment button so the focus stays on
                checkout. Buyer can opt in if inspired. */}
            <div className="mt-10 pt-6 border-t border-white/10" data-testid="addons-section">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55 text-center mb-1" data-testid="addons-heading">
                Would you like to pre-order Metamorph?
              </p>
              <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/35 text-center mb-4">
                If so, click below
              </p>
              <div className="flex flex-col gap-2" data-testid="addons-list">
                {/* WAV — pay what you can */}
                <div
                  className="border border-white/15 px-3 py-2.5 hover:border-white/35 transition"
                  style={addons.wav ? { borderColor: "rgba(243,224,166,0.5)", background: "rgba(243,224,166,0.04)" } : undefined}
                >
                  <label
                    className="flex items-center justify-between gap-3 cursor-pointer select-none"
                    data-testid="addon-wav-label"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className={`w-3.5 h-3.5 border flex items-center justify-center ${addons.wav ? "border-[var(--mm-accent)] bg-[var(--mm-accent)]/15" : "border-white/30"}`}>
                        {addons.wav && <Check className="w-2.5 h-2.5" strokeWidth={2.4} style={{ color: "var(--mm-accent)" }} />}
                      </span>
                      <input
                        type="checkbox"
                        checked={addons.wav}
                        onChange={() => setAddons((p) => ({ ...p, wav: !p.wav }))}
                        className="sr-only"
                        data-testid="addon-wav"
                      />
                      <span>
                        <span className="block text-[12px] text-white/90">Exclusive WAV download</span>
                        <span className="block font-mono text-[9px] uppercase tracking-[0.22em] text-white/45 mt-0.5">Pay what you can</span>
                      </span>
                    </div>
                  </label>
                  {addons.wav && (
                    <div className="flex items-center gap-2 mt-2 pl-6" data-testid="addon-wav-amount-row">
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">{currencySymbol}</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={wavAmount}
                        onChange={(e) => setWavAmount(e.target.value)}
                        className="w-20 bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-2 py-0.5 text-[12px] text-white tabular-nums"
                        data-testid="addon-wav-amount"
                      />
                      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">add to order</span>
                    </div>
                  )}
                </div>

                {/* Vinyl — reservation only (no charge) */}
                <label
                  className="flex items-start gap-2.5 cursor-pointer border border-white/15 px-3 py-2.5 hover:border-white/35 transition select-none"
                  style={addons.vinylReserve ? { borderColor: "rgba(243,224,166,0.5)", background: "rgba(243,224,166,0.04)" } : undefined}
                  data-testid="addon-vinyl-label"
                >
                  <span className={`w-3.5 h-3.5 border flex items-center justify-center mt-0.5 ${addons.vinylReserve ? "border-[var(--mm-accent)] bg-[var(--mm-accent)]/15" : "border-white/30"}`}>
                    {addons.vinylReserve && <Check className="w-2.5 h-2.5" strokeWidth={2.4} style={{ color: "var(--mm-accent)" }} />}
                  </span>
                  <input
                    type="checkbox"
                    checked={addons.vinylReserve}
                    onChange={() => setAddons((p) => ({ ...p, vinylReserve: !p.vinylReserve }))}
                    className="sr-only"
                    data-testid="addon-vinyl"
                  />
                  <span>
                    <span className="block text-[12px] text-white/90">Reserve a Vinyl LP</span>
                    <span className="block text-[10px] text-white/55 mt-0.5 leading-relaxed">
                      No charge today &mdash; we&rsquo;ll email you a pre-order link when the pressing kicks off.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </>
        ) : (
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-white/55 text-center mt-12">
            Loading the show…
          </p>
        )}
      </div>
    </main>
  );
}
